import {BaseInstance, InstanceType} from './types';
import {vec3, vec4} from 'gl-matrix';

const CYLINDER_TOP_POINTS = 60;
const SPHERE_CIRCLE_POINTS = 60;
const SPHERE_NUM_SLICES = 60;
const FAN_RADIUS_RATIO = 3; // Bigger Circle radius : Smaller Circle Radius
const FAN_BASE_LENGTH = 4;

const computeNormals = (_positions: number[], indices: number[]): number[] => {
    let i, j, k: number;

    const positionsAndNormals: number[] = [];
    let positions: vec4[] = [];

    const numVertices = _positions.length / 3;
    for (let i = 0; i < numVertices; i++) {
        positions.push(vec4.fromValues(
            _positions[(i * 3)],
            _positions[(i * 3) + 1],
            _positions[(i * 3) + 2],
            1
        ));
    }

    let normals: vec4[] = [];
    for (let i = 0; i < positions.length; i++) {
        normals.push(vec4.fromValues(0, 0, 0, 0));
    }

    for (let i = 0; i < indices.length; i += 3) {
        let norm: vec3 = vec3.fromValues(0, 0, 0);

        //compute the normal of this triangle
        let v: number[] = [0, 0, 0];

        for (let k = 0; k < 3; k++) {
            v[k] = indices[i + k];
        }

        //the newell's method to calculate normal
        for (let k = 0; k < 3; k++) {
            for (let l = 0; l < 3; l++) {
                norm[l] +=
                    (positions[v[k]][(l + 1) % 3] - positions[v[(k + 1) % 3]][(l + 1) % 3])
                    * (positions[v[k]][(l + 2) % 3] + positions[v[(k + 1) % 3]][(l + 2) % 3]);
            }
        }
        vec3.normalize(norm, norm);


        for (k = 0; k < 3; k++) {
            normals[v[k]] = vec4.add(normals[v[k]], normals[v[k]], vec4.fromValues(norm[0], norm[1], norm[2], 0));
        }
    }

    for (i = 0; i < normals.length; i++) {
        let n: vec3 = vec3.fromValues(normals[i][0], normals[i][1], normals[i][2]);
        vec3.normalize(n, n);
        normals[i] = vec4.fromValues(n[0], n[1], n[2], 0);
    }

    for (let i = 0; i < numVertices; i++) {
        positionsAndNormals.push(
            positions[i][0],
            positions[i][1],
            positions[i][2],
            normals[i][0],
            normals[i][1],
            normals[i][2],
        );
    }

    return positionsAndNormals;
}


/**
 * This function generates a unit cube centered at the origin. Tis means the the largest value for a particular axis
 * will be |.5|
 *
 * @param gl the {@link WebGLRenderingContext} to create the instance within
 * @returns a {@link BaseInstance} containing the newly generated cube instance
 */
export const generateCubeInstance = (gl: WebGLRenderingContext): BaseInstance => {
    // Instantiate the buffers
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const vertices: number[] = [];

    /*
    Looking as if x,y is 2d and z coming out.

    (-0.5, -0.5, 0.5) Front (z) BL
    (0.5, -0.5, 0.5) Front (z) BR
    (0.5, 0.5, 0.5) Front (z) TR
    (-0.5, 0.5, 0.5) Front (z) TL
    (-0.5, -0.5, -0.5) Back (z) BL
    (0.5, -0.5, -0.5) Back (z) BR
    (0.5, 0.5, -0.5) Back (z) TR
    (-0.5, 0.5, -0.5) Back (z) TL
    */

    const n = 1 / Math.sqrt(3);

    vertices.push(-0.5, -0.5, 0.5);  // FBL IDX: 0
    vertices.push(-n, -n, n);  // FBL IDX: 0
    vertices.push(0.5, -0.5, 0.5);   // FBR IDX: 1
    vertices.push(n, -n, n);   // FBR IDX: 1
    vertices.push(0.5, 0.5, 0.5);    // FTR IDX: 2
    vertices.push(n, n, n);    // FTR IDX: 2
    vertices.push(-0.5, 0.5, 0.5);   // FTL IDX: 3
    vertices.push(-n, n, n);   // FTL IDX: 3
    vertices.push(-0.5, -0.5, -0.5); // BBL IDX: 4
    vertices.push(-n, -n, -n); // BBL IDX: 4
    vertices.push(0.5, -0.5, -0.5);  // BBR IDX: 5
    vertices.push(n, -n, -n);  // BBR IDX: 5
    vertices.push(0.5, 0.5, -0.5);   // BTR IDX: 6
    vertices.push(n, n, -n);   // BTR IDX: 6
    vertices.push(-0.5, 0.5, -0.5);  // BTL IDX: 7
    vertices.push(-n, n, -n);  // BTL IDX: 7

    // Indices
    const indices: number[] = [];

    /*

    Front Face    (0, 2, 3), (0, 1, 2)
    Right Face     (1, 6, 2), (1, 5, 6)
    Back Face     (4, 6, 5), (4, 7, 6)
    Left Face    (4, 3, 0), (4, 7, 3)

    (Top Face)    (3, 2, 6), (3, 6, 7)
    (Bottom Face) (0, 5, 1), (0, 4, 5)

    */

    indices.push(0, 2, 3, 0, 1, 2); // front
    indices.push(1, 6, 2, 1, 5, 6); // right
    indices.push(5, 6, 7, 5, 4, 7); // back
    indices.push(4, 3, 7, 4, 0, 3); // left

    indices.push(3, 2, 6, 3, 6, 7); // top
    indices.push(4, 1, 0, 4, 5, 1); // bottom

    // const verticesAndNormals = computeNormals(vertices, indices);
    const verticesAndNormals = vertices;

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verticesAndNormals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {id: InstanceType.CUBE, vbo, ibo, numVertices: 8, numIndices: 36};
}

/**
 * This function generates a unit room centered at the origin. Tis means the the largest value for a particular axis
 * will be |.5|. A room is basically an inverted box, so that we want to see the inside of the base instance.
 *
 * @param gl the {@link WebGLRenderingContext} to create the instance within
 * @returns a {@link BaseInstance} containing the newly generated room instance
 */
export const generateRoomInstance = (gl: WebGLRenderingContext): BaseInstance => {
    // Instantiate the buffers
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const vertices: number[] = [];

    /*
    Looking as if x,y is 2d and z coming out.

    (-0.5, -0.5, 0.5) Front (z) BL
    (0.5, -0.5, 0.5) Front (z) BR
    (0.5, 0.5, 0.5) Front (z) TR
    (-0.5, 0.5, 0.5) Front (z) TL
    (-0.5, -0.5, -0.5) Back (z) BL
    (0.5, -0.5, -0.5) Back (z) BR
    (0.5, 0.5, -0.5) Back (z) TR
    (-0.5, 0.5, -0.5) Back (z) TL
    */

    const n = 1 / Math.sqrt(3);

    vertices.push(-0.5, -0.5, 0.5);  // FBL IDX: 0
    vertices.push(n, n, -n);  // FBL IDX: 0
    vertices.push(0.5, -0.5, 0.5);   // FBR IDX: 1
    vertices.push(-n, n, -n);   // FBR IDX: 1
    vertices.push(0.5, 0.5, 0.5);    // FTR IDX: 2
    vertices.push(-n, -n, -n);    // FTR IDX: 2
    vertices.push(-0.5, 0.5, 0.5);   // FTL IDX: 3
    vertices.push(n, -n, -n);   // FTL IDX: 3
    vertices.push(-0.5, -0.5, -0.5); // BBL IDX: 4
    vertices.push(n, n, n); // BBL IDX: 4
    vertices.push(0.5, -0.5, -0.5);  // BBR IDX: 5
    vertices.push(-n, n, n);  // BBR IDX: 5
    vertices.push(0.5, 0.5, -0.5);   // BTR IDX: 6
    vertices.push(-n, -n, n);   // BTR IDX: 6
    vertices.push(-0.5, 0.5, -0.5);  // BTL IDX: 7
    vertices.push(n, -n, n);  // BTL IDX: 7

    // Indices
    const indices: number[] = [];

    // REVERSE THE CUBE INDICES
    indices.push(0, 2, 3, 0, 1, 2); // front
    indices.push(1, 6, 2, 1, 5, 6); // right
    indices.push(5, 6, 7, 5, 4, 7); // back
    indices.push(4, 3, 7, 4, 0, 3); // left

    indices.push(3, 2, 6, 3, 6, 7); // top
    indices.push(4, 1, 0, 4, 5, 1); // bottom

    // const verticesAndNormals = computeNormals(vertices, indices);
    const verticesAndNormals = vertices;

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verticesAndNormals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {id: InstanceType.CUBE, vbo, ibo, numVertices: 8, numIndices: 36};
}

/**
 * This function generates a cylinder centered at the origin. It is completely encompassed
 * within a unit cube, so it has a radius of 0.5 an a height of 1.
 *
 * @param gl the {@link WebGLRenderingContext} to create the instance within
 * @returns a {@link BaseInstance} containing the newly generated cube instance
 */
export const generateCylinderInstance = (gl: WebGLRenderingContext): BaseInstance => {
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    // Top circle indices will be even, bottom odd
    const vertices = [];
    const indices = [];
    for (let i = 0; i < CYLINDER_TOP_POINTS; i++) {
        const theta = 2 * Math.PI * i / CYLINDER_TOP_POINTS;
        const x = 0.5 * Math.cos(theta);
        const y = 0.5 * Math.sin(theta);
        vertices.push(x, y, 0.5, x, y, -0.5);
        const topIndex = i * 2;
        const bottomIndex = (i * 2) + 1;
        const nextI = (i + 1) % CYLINDER_TOP_POINTS;
        const nextTopIndex = nextI * 2;
        const nextBottomIndex = (nextI * 2) + 1;
        indices.push(topIndex, bottomIndex, nextTopIndex);
        indices.push(bottomIndex, nextBottomIndex, nextTopIndex);
    }

    const verticesAndNormals = computeNormals(vertices, indices);

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verticesAndNormals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {
        id: InstanceType.CYLINDER,
        vbo,
        ibo,
        numVertices: CYLINDER_TOP_POINTS * 6,
        numIndices: CYLINDER_TOP_POINTS * 6
    };
}

/**
 * This function generates a unit sphere centered at the origin. This means that the points
 * intersecting the x, y, and z axes are at most |.5|.
 *
 * @returns a {@link BaseInstance} containing the instance with the unit circle
 * @param gl the {@link WebGLRenderingContext} we want to create the sphere within
 */
export const generateSphereInstance = (gl: WebGLRenderingContext): BaseInstance => {
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const r = 0.5;
    const vertices = [];
    const indices = [];
    const topPoint = 0;
    vertices.push(0, 0, 0.5); // top point
    // draw the top cap
    for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
        const circlePoint = i + 1;
        const circleNextPoint = ((i + 1) % SPHERE_CIRCLE_POINTS) + 1;

        indices.push(topPoint, circlePoint, circleNextPoint);
    }
    for (let s = 1; s < SPHERE_NUM_SLICES; s++) {
        const z = 0.5 - (s / SPHERE_NUM_SLICES);
        const sliceR = Math.sqrt(r * r - z * z); // radius of the slice
        const circleFirstPoint = 1 + SPHERE_CIRCLE_POINTS * (s - 1);
        const nextCircleFirstPoint = circleFirstPoint + SPHERE_CIRCLE_POINTS;
        for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
            const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
            const x = sliceR * Math.cos(theta);
            const y = sliceR * Math.sin(theta);
            vertices.push(x, y, z);

            if (s !== SPHERE_NUM_SLICES - 1) {
                const point = i;
                const nextPoint = (i + 1) % SPHERE_CIRCLE_POINTS;
                const circlePoint = circleFirstPoint + point;
                const circleNextPoint = circleFirstPoint + nextPoint;
                const nextCirclePoint = nextCircleFirstPoint + point;
                const nextCircleNextPoint = nextCircleFirstPoint + nextPoint;

                indices.push(circlePoint, nextCirclePoint, circleNextPoint);
                indices.push(nextCirclePoint, nextCircleNextPoint, circleNextPoint);
            }
        }
    }
    // Draw the bottom point
    const bottomPoint = (vertices.length / 3);
    vertices.push(0, 0, -0.5);
    // Draw the bottom cap
    const lastSliceFirstPoint = bottomPoint - SPHERE_CIRCLE_POINTS;
    for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
        const circlePoint = i + lastSliceFirstPoint;
        const circleNextPoint = ((i + 1) % SPHERE_CIRCLE_POINTS) + lastSliceFirstPoint;

        indices.push(bottomPoint, circlePoint, circleNextPoint);
    }

    const verticesAndNormals = computeNormals(vertices, indices);

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verticesAndNormals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {
        id: InstanceType.SPHERE,
        vbo,
        ibo,
        numVertices: vertices.length / 6,
        numIndices: indices.length
    };
};

/**
 * This function generates a unit semi-sphere centered at the origin. This is like the
 * sphere instance, except it doesn't go past z = 0 (stays at z >= 0).
 *
 * @returns a {@link BaseInstance} containing the instance with the unit circle
 * @param gl the {@link WebGLRenderingContext} we want to create the sphere within
 */
export const generateSemisphereInstance = (gl: WebGLRenderingContext): BaseInstance => {
    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const r = 0.5;
    const vertices = [];
    const indices = [];
    const topPoint = 0;
    vertices.push(0, 0, 0.5); // top point
    // draw the top cap
    for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
        const circlePoint = i + 1;
        const circleNextPoint = ((i + 1) % SPHERE_CIRCLE_POINTS) + 1;

        indices.push(topPoint, circlePoint, circleNextPoint);
    }
    for (let s = 1; s < SPHERE_NUM_SLICES / 2; s++) {
        const z = 0.5 - (s / SPHERE_NUM_SLICES);
        const sliceR = Math.sqrt(r * r - z * z); // radius of the slice
        const circleFirstPoint = 1 + SPHERE_CIRCLE_POINTS * (s - 1);
        const nextCircleFirstPoint = circleFirstPoint + SPHERE_CIRCLE_POINTS;
        for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
            const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
            const x = sliceR * Math.cos(theta);
            const y = sliceR * Math.sin(theta);
            vertices.push(x, y, z);

            if (s + 1 < (SPHERE_NUM_SLICES / 2)) {
                const point = i;
                const nextPoint = (i + 1) % SPHERE_CIRCLE_POINTS;
                const circlePoint = circleFirstPoint + point;
                const circleNextPoint = circleFirstPoint + nextPoint;
                const nextCirclePoint = nextCircleFirstPoint + point;
                const nextCircleNextPoint = nextCircleFirstPoint + nextPoint;

                indices.push(circlePoint, nextCirclePoint, circleNextPoint);
                indices.push(nextCirclePoint, nextCircleNextPoint, circleNextPoint);
            }
        }
    }
    // Draw the bottom point
    const bottomPoint = vertices.length / 3;
    vertices.push(0, 0, 0);
    // Draw the bottom circle
    const lastSliceFirstPoint = bottomPoint - SPHERE_CIRCLE_POINTS;
    for (let i = 0; i < SPHERE_CIRCLE_POINTS; i++) {
        const circlePoint = i + lastSliceFirstPoint;
        const circleNextPoint = ((i + 1) % SPHERE_CIRCLE_POINTS) + lastSliceFirstPoint;

        indices.push(bottomPoint, circleNextPoint, circlePoint);
    }

    const verticesAndNormals = computeNormals(vertices, indices);

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verticesAndNormals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {
        id: InstanceType.SEMI_SPHERE,
        vbo,
        ibo,
        numVertices: vertices.length / 6,
        numIndices: indices.length
    };
};

/**
 * This function creates a complex fan shape that looks like a smaller and a larger
 * circle connected at their furthest points. It is completely bounded within a
 *
 * @returns a {@link BaseInstance} containing the instance with the fan shape.
 * @param gl the {@link WebGLRenderingContext} we want to create the fan within
 */
export const generateFanInstance = (gl: WebGLRenderingContext): BaseInstance => {

    const vbo = gl.createBuffer();
    const ibo = gl.createBuffer();

    const vertices = [];
    const indices = [];

    const r_large: number = 0.5;
    const r_small: number = r_large / FAN_RADIUS_RATIO;
    // top fan face ==>

    const large_circle_x = FAN_BASE_LENGTH - r_large;
    const large_circle_theta = Math.acos(r_large / ((large_circle_x - r_small) * FAN_RADIUS_RATIO));
    const small_circle_theta = Math.PI - large_circle_theta;
    const small_circle_start_point = Math.round(SPHERE_CIRCLE_POINTS * small_circle_theta / (Math.PI * 2));
    const small_circle_end_point = Math.round(SPHERE_CIRCLE_POINTS * (Math.PI * 2 - small_circle_theta) / (Math.PI * 2));
    const large_circle_start_point = Math.round(SPHERE_CIRCLE_POINTS * ((large_circle_theta - Math.PI) / (Math.PI * 2)));
    const large_circle_end_point = Math.round(SPHERE_CIRCLE_POINTS * ((Math.PI - large_circle_theta) / (Math.PI * 2)));

    // Small semi circle
    const small_circle_index = (vertices.length / 3);
    const small_circle_top = small_circle_index + 1;
    let small_circle_bottom: number = -1;
    vertices.push(r_small, 0, 0.5);
    for (let i = small_circle_start_point; i <= small_circle_end_point; i++) {
        const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
        const x = r_small * Math.cos(theta);
        const y = r_small * Math.sin(theta);
        vertices.push(x + r_small, y, 0.5);

        if ((i + 1) <= small_circle_end_point) {
            indices.push(small_circle_index, (vertices.length / 3) - 1, (vertices.length / 3));
        } else {
            small_circle_bottom = (vertices.length / 3) - 1;
        }
    }

    // Large semi circle
    const large_circle_index = (vertices.length / 3);
    const large_circle_bottom = large_circle_index + 1;
    let large_circle_top: number = -1;
    vertices.push(large_circle_x, 0, 0.5);
    for (let i = large_circle_start_point; i <= large_circle_end_point; i++) {
        const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
        const x = r_large * Math.cos(theta);
        const y = r_large * Math.sin(theta);
        vertices.push(x + large_circle_x, y, 0.5);

        if ((i + 1) <= large_circle_end_point) {
            indices.push(large_circle_index, (vertices.length / 3) - 1, (vertices.length / 3));
        } else {
            large_circle_top = (vertices.length / 3) - 1;
        }
    }

    // Triangles connecting the semicircles
    indices.push(small_circle_top, small_circle_bottom, large_circle_bottom);
    indices.push(large_circle_bottom, large_circle_top, small_circle_top);

    // Fill in last bits
    indices.push(large_circle_bottom, large_circle_index, large_circle_top);

    const offset = (vertices.length / 3);

    // bottom fan face ==>
    // Small semi circle
    const bottom_small_circle_index = (vertices.length / 3);
    const bottom_small_circle_top = bottom_small_circle_index + 1;
    let bottom_small_circle_bottom: number = -1;
    vertices.push(r_small, 0, -0.5);
    for (let i = small_circle_start_point; i <= small_circle_end_point; i++) {
        const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
        const x = r_small * Math.cos(theta);
        const y = r_small * Math.sin(theta);
        vertices.push(x + r_small, y, -0.5);

        if ((i + 1) <= small_circle_end_point) {
            indices.push(bottom_small_circle_index, (vertices.length / 3) - 1, (vertices.length / 3));
        } else {
            bottom_small_circle_bottom = (vertices.length / 3) - 1;
        }
    }

    // Large semi circle
    const bottom_large_circle_index = (vertices.length / 3);
    const bottom_large_circle_bottom = bottom_large_circle_index + 1;
    let bottom_large_circle_top: number = -1;
    vertices.push(large_circle_x, 0, -0.5);
    for (let i = large_circle_start_point; i <= large_circle_end_point; i++) {
        const theta = 2 * Math.PI * i / SPHERE_CIRCLE_POINTS;
        const x = r_large * Math.cos(theta);
        const y = r_large * Math.sin(theta);
        vertices.push(x + large_circle_x, y, -0.5);

        if ((i + 1) <= large_circle_end_point) {
            indices.push(bottom_large_circle_index, (vertices.length / 3) - 1, (vertices.length / 3));
        } else {
            bottom_large_circle_top = (vertices.length / 3) - 1;
        }
    }

    // Triangles connecting the semicircles
    indices.push(bottom_small_circle_top, bottom_small_circle_bottom, bottom_large_circle_bottom);
    indices.push(bottom_large_circle_bottom, bottom_large_circle_top, bottom_small_circle_top);

    // Fill in last bits
    indices.push(bottom_large_circle_bottom, bottom_large_circle_index, bottom_large_circle_top);

    // Fill in middle using corresponding indices
    for (let i = 0; i < offset - 1; i++) {
        const topFirst = i;
        const topNext = topFirst + 1;
        const bottomFirst = offset + i;
        const bottomNext = bottomFirst + 1;
        indices.push(topFirst, bottomFirst, bottomNext);
        indices.push(bottomNext, topNext, topFirst);
    }

    const verticesAndNormals = computeNormals(vertices, indices);

    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verticesAndNormals), gl.STATIC_DRAW);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);

    return {
        id: InstanceType.FAN,
        vbo,
        ibo,
        numVertices: vertices.length / 6,
        numIndices: indices.length
    };
};
